import * as Sentry from '@sentry/react'

import * as types from 'models/actionTypes'
import * as tracker from 'utils/tracker'
import { removeNewUser } from 'services/user'
import { getCurrentUser, getName } from 'models/user/selectors'
import { getRenderer } from 'models/refStore/selectors'
import { getSelectedCompositionTrackingRoomInfo, getUserDidAddRenderItem } from 'models/compositions/selectors'
import {
    getScheduledScreenshotTimeout,
    getFirstScheduledScreenshotTime,
    getFirstFurnitureScreenshotDone,
} from 'models/tracking/selectors'
import { OPTIMIZELY_ADD_TO_CART_MW, OPTIMIZELY_ADD_TO_CART } from 'models/tracking/constants'
import {
    scheduleDelayedScreenshot,
    delayedScreenshotScsheduled,
    delayedScreenshotSent,
    firstRenderItemScreenshotDone,
} from 'models/compositions/actions'
import { getInitialLoading, getIsMagicWindowLoading } from 'models/preview/selectors'
import { getSharePanelOpen } from 'models/ui/selectors'
import { STATE_DONE } from 'constants/stateTypes'
import { DESIGN_SIDEPANEL_MODE_FAVORITES_BROWSE } from 'models/ui/constants'
import { getNumFavorites } from 'models/favorites/selectors'
import * as msgTypes from 'models/iFrame/msgTypes'

import uploadPreviewScreenshot from 'utils/uploadPreviewScreenshot'
import { getAddToCartUrl } from 'utils/buildUrls'

const trackingMiddleware = (store) => (next) => (action) => {
    const result = next(action)
    const state = store.getState()
    const roomInfo = getSelectedCompositionTrackingRoomInfo(state)

    switch (action.type) {
        case types.RECEIVE_USER: {
            const { wid, metricsId, email, isNewUser, isLoggedIn, loginType } = getCurrentUser(state)
            const trackingId = metricsId || wid

            if (isNewUser) {
                tracker.alias(trackingId)
                tracker.set({
                    UserId: wid,
                    UserEmail: email,
                    LoginAuthenticationMode: loginType,
                    Name: getName(state),
                    SignupDate: new Date(),
                    LastLogin: new Date(),
                })
                removeNewUser()
            } else if (isLoggedIn) {
                tracker.set({
                    UserId: wid,
                    UserEmail: email,
                    LoginAuthenticationMode: loginType,
                    Name: getName(state),
                    LastLogin: new Date(),
                })
                tracker.identify(trackingId)
            }
            if (isLoggedIn) {
                tracker.initPendo(trackingId, { email })
                Sentry.configureScope((scope) => {
                    scope.setUser({ id: wid })
                })
            } else {
                tracker.initPendo()
            }
            break
        }
        case types.FAILED_RECEIVE_USER:
            tracker.initPendo()
            break
        case types.PLACE_RENDER_ITEM:
            tracker.loadGuides()
            tracker.drop('DecorateRoom.AddProduct', {
                ...action.trackingInfo,
                ...roomInfo,
            })
            break
        case types.DELETE_COMPOSITION_RENDER_ITEM: {
            if (action.trackingInfo) {
                tracker.click('DecorateRoom.RemoveProduct', {
                    ...action.trackingInfo,
                    ...roomInfo,
                })
            }
            const renderer = getRenderer()
            if (renderer) {
                const isMagicWindowLoading = getIsMagicWindowLoading(state)
                const histogram = renderer.getHistogramStats()
                tracker.workflow('DecorateRoom.Histogram', {
                    ...histogram,
                    isMagicWindowLoading,
                })
            }
            store.dispatch(scheduleDelayedScreenshot())
            break
        }
        case types.SWAP_RENDER_ITEM: {
            const {
                trackingInfo: { swappedInTrackingInfo, removedTrackingInfo },
            } = action
            if (swappedInTrackingInfo) {
                tracker.drop('DecorateRoom.AddProduct', {
                    ...swappedInTrackingInfo,
                    ...roomInfo,
                })
            }
            if (removedTrackingInfo) {
                tracker.click('DecorateRoom.RemoveProduct', {
                    ...removedTrackingInfo,
                    ...roomInfo,
                })
            }
            break
        }
        case types.RENDER_ITEM_LOADED:
        case types.WALLART_ITEM_LOADED: {
            const renderer = getRenderer()
            if (renderer) {
                const isMagicWindowLoading = getIsMagicWindowLoading(state)
                const histogram = renderer.getHistogramStats()
                tracker.workflow('DecorateRoom.Histogram', {
                    ...histogram,
                    isMagicWindowLoading,
                })
            }
            const userDidAddRenderItem = getUserDidAddRenderItem(state)
            const isInitialLoading = getInitialLoading(state)
            const firstFurnitureScreenshotDone = getFirstFurnitureScreenshotDone(state)
            if (!isInitialLoading && userDidAddRenderItem && !firstFurnitureScreenshotDone) {
                store.dispatch(firstRenderItemScreenshotDone())

                const fileName = 'screenshot.jpg'
                uploadPreviewScreenshot(fileName, window.globalRendererSessionId)
                    .then(({ uuid, imageIsBlack }) => {
                        const eventName = 'DecorateRoom.Screenshot'
                        const { wid } = getCurrentUser(state)
                        tracker.workflow(eventName, {
                            userId: wid,
                            sessionId: window.globalRendererSessionId,
                            uuid,
                            imageIsBlack,
                            screenshotType: 'FirstProductAdd',
                            minuteIncrement: 0,
                            ...roomInfo,
                        })
                    })
                    .catch(() => {
                        const eventName = 'DecorateRoom.ScreenshotFailed'
                        const { wid } = getCurrentUser(state)
                        tracker.workflow(eventName, {
                            userId: wid,
                            sessionId: window.globalRendererSessionId,
                            ...roomInfo,
                        })
                    })
            }
            store.dispatch(scheduleDelayedScreenshot())
            break
        }
        case types.UPDATE_COMPOSITION_RENDER_ITEM: {
            const {
                item: { x, y, heading },
                trackingInfo,
            } = action
            if (x || y || heading) {
                const eventName = heading ? 'DecorateRoom.RotateProduct' : 'DecorateRoom.MoveProduct'
                tracker.drop(eventName, {
                    ...trackingInfo,
                    ...roomInfo,
                })
                const renderer = getRenderer()
                if (renderer) {
                    const isMagicWindowLoading = getIsMagicWindowLoading(state)
                    const histogram = renderer.getHistogramStats()
                    tracker.workflow('DecorateRoom.Histogram', {
                        ...histogram,
                        isMagicWindowLoading,
                    })
                }
            } else {
                tracker.click('DecorateRoom.ChangeProperties', {
                    ...trackingInfo,
                    ...roomInfo,
                })
            }
            store.dispatch(scheduleDelayedScreenshot())
            break
        }
        case types.REQUEST_SAVE_COMPOSITION: {
            const { trackingInfo } = action
            tracker.click('DecorateRoom.SaveDesign', {
                DesignName: action.data.name,
                DesignId: action.data.compositionId,
                SaveType: action.data.compositionId === -1 ? 'New' : 'Edit',
                ...roomInfo,
                ...trackingInfo,
            })

            if (action.data.compositionId === -1) {
                tracker.increment('DesignsSaved')
            }
            break
        }
        case types.OPEN_COMPOSITION:
            if (action.trackingInfo) {
                tracker.click('DecorateRoom.OpenDesign', {
                    ...action.trackingInfo,
                })
            }
            break
        case types.FAILED_RECEIVE_SCENES:
            tracker.error('LoadingRooms')
            break
        case types.NEW_RENDERER: {
            const randNums = new Uint32Array(1)
            window.crypto.getRandomValues(randNums)
            window.globalRendererSessionId = `${Date.now()}-${randNums[0]}`
            tracker.initDesignFeedbackTriggerEvents(roomInfo)
            if (!getRenderer().isRendererValid()) {
                tracker.error('NullWebGLContextInfo')
            }

            break
        }
        case types.RENDERER_READY:
            tracker.track('WebGLContextInfo', action.contextInfo)
            break
        case types.SET_INITIAL_LOADING:
            if (!action.initialLoading) {
                let totalSceneLoadSizeMB = 0.0
                let modelsCount = 0
                const loadCompleteTime = tracker.getTimeSinceLoadingStart()
                const renderer = getRenderer()
                if (renderer) {
                    const additionalStats = renderer.getRoomAdditionalStats()
                    const glbStats = renderer.getRoomGlbStats()
                    totalSceneLoadSizeMB = additionalStats.sceneAdditionalDataSizeMB + glbStats.totalModelsLoadSizeMB
                    modelsCount = glbStats.modelsCount
                }

                tracker.track('DecorateRoom.FullLoadComplete', {
                    ...roomInfo,
                    loadCompleteTime,
                    totalSceneLoadSizeMB,
                    modelsCount,
                })

                const fileName = 'screenshot.jpg'
                uploadPreviewScreenshot(fileName, window.globalRendererSessionId)
                    .then(({ uuid, imageIsBlack }) => {
                        const eventName = 'DecorateRoom.Screenshot'
                        const { wid } = getCurrentUser(state)
                        tracker.workflow(eventName, {
                            userId: wid,
                            sessionId: window.globalRendererSessionId,
                            uuid,
                            imageIsBlack,
                            screenshotType: 'InitialLoad',
                            minuteIncrement: 0,
                            ...roomInfo,
                        })
                    })
                    .catch(() => {
                        const eventName = 'DecorateRoom.ScreenshotFailed'
                        const { wid } = getCurrentUser(state)
                        tracker.workflow(eventName, {
                            userId: wid,
                            sessionId: window.globalRendererSessionId,
                            ...roomInfo,
                        })
                    })
            } else {
                tracker.markLoadingStart()
            }
            break
        case types.SCHEDULE_DELAYED_SCREENSHOT:
            {
                const currentTimeoutId = getScheduledScreenshotTimeout(state)
                if (!currentTimeoutId) {
                    const screenshotTimeout = 60 * 1000
                    const newTimeout = window.setTimeout(() => {
                        const firstScreenshotTime = getFirstScheduledScreenshotTime(state)
                        const minuteIncrement = Math.round(
                            (window.performance.now() - firstScreenshotTime) / (1000.0 * 60.0),
                        )
                        const fileName = 'screenshot.jpg'
                        uploadPreviewScreenshot(fileName, window.globalRendererSessionId)
                            .then(({ uuid, imageIsBlack }) => {
                                const eventName = 'DecorateRoom.Screenshot'
                                const { wid } = getCurrentUser(state)
                                tracker.workflow(eventName, {
                                    userId: wid,
                                    sessionId: window.globalRendererSessionId,
                                    uuid,
                                    imageIsBlack,
                                    screenshotType: 'MinuteIncrement',
                                    minuteIncrement,
                                    ...roomInfo,
                                })
                                store.dispatch(delayedScreenshotSent())
                            })
                            .catch(() => {
                                const eventName = 'DecorateRoom.ScreenshotFailed'
                                const { wid } = getCurrentUser(state)
                                tracker.workflow(eventName, {
                                    userId: wid,
                                    sessionId: window.globalRendererSessionId,
                                    ...roomInfo,
                                })
                            })
                    }, screenshotTimeout)
                    store.dispatch(delayedScreenshotScsheduled(newTimeout))
                }
            }
            break
        case types.DESTROY_RENDERER:
            {
                window.globalRendererSessionId = null
                tracker.cancelDesignFeedbackTriggerEvents()
                const currentTimeoutId = getScheduledScreenshotTimeout(state)
                if (currentTimeoutId) {
                    window.clearTimeout(currentTimeoutId)
                }
            }
            break
        case types.SHARE_PANEL_OPEN_TOGGLE:
            // If we're opening the panel
            if (getSharePanelOpen(state)) {
                tracker.click('DecorateRoom.ShareDesignCTA')
            }
            break
        case types.ADD_TO_BAG: {
            const eventName = 'DecorateRoom.AddProductToBag'
            const cartUrl = getAddToCartUrl([action.localItemNumber], action.region)
            const trackingInfo = {
                ...action.trackingInfo,
                CartUrl: cartUrl,
            }
            tracker.click(eventName, {
                ...trackingInfo,
                ...roomInfo,
            })

            const isMagicWindow = tracker.getIsMagicWindow()
            if (isMagicWindow) {
                tracker.optimizelyTrack(OPTIMIZELY_ADD_TO_CART_MW)
                tracker.optimizelyTrack(OPTIMIZELY_ADD_TO_CART)
            }

            break
        }
        case types.ADD_ROOM_TO_BAG: {
            const eventName = 'DecorateRoom.AddRoomToBag'
            const { localItemNumbers, region, trackingInfo } = action
            const cartUrl = getAddToCartUrl(localItemNumbers, region)
            const updatedTrackingInfo = {
                ...trackingInfo,
                CartUrl: cartUrl,
            }
            tracker.click(eventName, {
                ...updatedTrackingInfo,
                ...roomInfo,
            })

            // For each item added to cart, call optimizely add to cart event, global and from magic window
            const isMagicWindow = tracker.getIsMagicWindow()
            if (localItemNumbers.length && isMagicWindow) {
                localItemNumbers.forEach(() => {
                    tracker.optimizelyTrack(OPTIMIZELY_ADD_TO_CART_MW)
                    tracker.optimizelyTrack(OPTIMIZELY_ADD_TO_CART)
                })
            }
            break
        }
        case types.OPEN_BAG:
            tracker.click('DecorateRoom.OpenBag', { ...roomInfo })
            break
        case types.TRACK_SHOW_DETAILS: {
            const eventName = 'DecorateRoom.GetAdditionalDetails'
            tracker.click(eventName, {
                ...action.trackingInfo,
                ...roomInfo,
            })
            break
        }
        case types.SET_MAGIC_WINDOW_LOADING: {
            // Only track if finished loading
            if (!action.isMagicWindowLoading) {
                // happens when MW loading animation switches to the scene view
                const eventName = 'DecorateRoom.LoadComplete'
                const loadCompleteTime = tracker.getTimeSinceLoadingStart()

                let glbStats = {}
                const renderer = getRenderer()
                if (renderer) {
                    glbStats = renderer.getRoomGlbStats()
                }
                tracker.track(eventName, {
                    ...roomInfo,
                    loadCompleteTime,
                    ...glbStats,
                })
            }
            break
        }
        case types.OPEN_DESIGN_SIDEPANEL:
        case types.SET_DESIGN_SIDEPANEL_MODE: {
            const { trackingInfo, mode } = action
            // Only track when there is a productId/title/accessFrom fields
            if (trackingInfo && trackingInfo.ProductId) {
                tracker.click('DecorateRoom.GetInfo', {
                    ...trackingInfo,
                    ...roomInfo,
                })
            }
            if (mode === DESIGN_SIDEPANEL_MODE_FAVORITES_BROWSE) {
                tracker.click('DecorateRoom.Favorites', {
                    ...roomInfo,
                    Favorites: getNumFavorites(state),
                })
            }
            break
        }
        case types.TOGGLE_FAVORITE: {
            const { isFavorite, localItemNumber } = action
            tracker.click(isFavorite ? 'DecorateRoom.AddFavorites' : 'DecorateRoom.RemoveFavorites', {
                ...roomInfo,
                ProductLocalId: localItemNumber,
                Favorites: getNumFavorites(state),
            })
            break
        }
        case types.REMOVE_RENDER_ITEMS: {
            const eventName = 'DecorateRoom.ClearAllFurniture'
            tracker.click(eventName, { ...roomInfo })
            break
        }
        case types.RECEIVE_APP_DOWNLOAD_SMS: {
            const { status } = action
            const eventName = 'DecorateRoom.TryInMyRoomInfo.SendSMSResult'
            tracker.workflow(eventName, {
                ...roomInfo,
                Result: status === STATE_DONE ? 'Success' : 'Failure',
            })

            break
        }
        case types.UPDATE_PREVIEW_SETTINGS: {
            const { settings } = action
            if (settings && Object.prototype.hasOwnProperty.call(settings, 'magicEraserMode')) {
                let eventName
                if (settings.magicEraserMode) {
                    eventName = 'DecorateRoom.EraseMode'
                } else {
                    eventName = 'DecorateRoom.EraseMode.Exit'
                }
                tracker.workflow(eventName, {
                    ...roomInfo,
                })
            }
            break
        }
        case types.UPDATE_COMPOSITION_SAVE_DATA:
            if (action.saveData.erasedSegments && action.trackingInfo && action.trackingInfo.prevErasedSegments) {
                const nextSegments = action.saveData.erasedSegments
                const prevSegments = action.trackingInfo.prevErasedSegments
                // If the number of erased items has changed
                if (nextSegments.length !== prevSegments.length) {
                    let eventName
                    let eraseItemId
                    const diff = (ar1, ar2) => {
                        const remaining = ar1.filter((i) => !ar2.includes(i))
                        // We only get one item at a time in this action
                        return remaining[0]
                    }
                    if (nextSegments.length > prevSegments.length) {
                        eventName = 'DecorateRoom.EraseItemHidden'
                        eraseItemId = diff(nextSegments, prevSegments)
                    } else {
                        eventName = 'DecorateRoom.EraseItemShown'
                        eraseItemId = diff(prevSegments, nextSegments)
                    }

                    tracker.workflow(eventName, {
                        ...roomInfo,
                        EraseItemId: eraseItemId,
                        ErasedItems: nextSegments.length,
                        EraseableItems: action.trackingInfo.EraseableItems,
                    })
                }
            }
            break
        case types.NEW_IFRAME_MESSAGE:
            if (action.message && action.message.type === msgTypes.MAGIC_WINDOW_CLOSE) {
                tracker.workflow('DecorateRoom.MagicWindowExit', {
                    ...roomInfo,
                })
            }
            break
        case types.WEBGL_CONTEXT_LOST:
            tracker.workflow('DecorateRoom.WebGlContextLost', {
                ...roomInfo,
            })
            break
        case types.WEBGL_CONTEXT_NOT_CREATED:
            tracker.workflow('DecorateRoom.WebGlContextNotCreated', {
                ...roomInfo,
            })
            break
        case types.WEBGL_CONTEXT_RESTORED:
            tracker.workflow('DecorateRoom.WebGlContextRestored', {
                ...roomInfo,
            })
            break
        case types.SET_WALL_COLOR:
            tracker.click('DecorateRoom.WallColor.Select', {
                WallColorChoice: action.colorString,
                WallColorPlaneIndex: action.planeIndex,
                WallColorChangeId: action.trackingInfo.timestamp,
                ...roomInfo,
            })
            break
        case types.SHOW_LOGIN_PROMPT_MODAL:
        case types.LOGIN_NEEDED:
            if (action.type === types.LOGIN_NEEDED || action.showLoginPromptModal) {
                tracker.click('DecorateRoom.SignupLogin.Overlay')
            }
            break
        case types.INITIALIZATION_FAILURE:
            tracker.workflow('DecorateRoom.InitializationFailure', {
                ...action.info,
            })
            break
        default:
            break
    }

    return result
}

export default trackingMiddleware
